import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import ConfirmModal from "../common/ConfirmModal";
import { listAnnotations, addAnnotation, updateAnnotation, deleteAnnotation } from "../../api/annotationApi";
import "./annotationViewer.css";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

/**
 * Mark up an archived PDF without changing a byte of it.
 *
 * <b>The page is rendered, never edited.</b> pdf.js draws the page to a canvas and the marks are an SVG
 * layer sitting exactly on top. Nothing is written back into the file — there is no flatten step, no second
 * copy of the document, and therefore nothing that can drift out of step with the archived original.
 *
 * <b>Everything is stored in PDF user-space points, never pixels.</b> The conversion both ways is
 * <code>viewport.convertToPdfPoint</code> / <code>convertToViewportPoint</code>, which is the page's own
 * coordinate system and already accounts for scale and page rotation. Storing pixels would tie a mark to
 * the zoom level and screen of whoever drew it. PDF's Y axis points up from the bottom-left, the screen's
 * points down from the top-left, which is why every rectangle is normalised after conversion rather than
 * assumed to have a positive height.
 *
 * <b>Marks belong to one version.</b> The viewer asks for the marks on the document row it was opened with,
 * and reports separately when other versions carry marks of their own — it does not draw them, because
 * their coordinates were measured against different bytes.
 *
 * Props: open, blobUrl, docName, dbName, documentId, token, onClose
 */

const TOOLS = ["Select", "Highlight", "Box", "Strikeout", "Note", "Ink"];

// Deliberately a small fixed set: an arbitrary colour picker produces marks nobody can describe to a
// colleague ("the browny-orange one"), and these read clearly over both white paper and dark scans.
const COLORS = ["#ffd400", "#ff5252", "#22c55e", "#3b82f6", "#a855f7"];

const DEFAULT_COLOR = COLORS[0];
const MIN_DRAG_PX = 3;   // below this a drag is a click, not a rectangle

export default function AnnotationViewer({ open, blobUrl, docName, dbName, documentId, token, onClose }) {
  const { t } = useTranslation();

  const [pdf, setPdf] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [scale, setScale] = useState(1.25);
  const [viewport, setViewport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [marks, setMarks] = useState([]);
  const [onOtherVersions, setOnOtherVersions] = useState(0);
  const [versionNumber, setVersionNumber] = useState(null);
  const [busy, setBusy] = useState(false);

  const [tool, setTool] = useState("Select");
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [drag, setDrag] = useState(null);          // { x0, y0, x1, y1 } in canvas pixels
  const [inkPath, setInkPath] = useState(null);    // [[px,py], …] in canvas pixels
  const [noteDraft, setNoteDraft] = useState(null); // { xPt, yPt, text }
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  // ── Load the document ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !blobUrl) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const buf = await (await fetch(blobUrl)).arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;
        setPdf(doc);
        setPageCount(doc.numPages);
        setPageNo(1);
      } catch (e) {
        if (!cancelled) {
          setError(t("annotations.notReadable", {
            defaultValue: "This document could not be opened as a PDF, so it cannot be annotated.",
          }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, blobUrl, t]);

  // ── Render the current page ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdf || !open) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const page = await pdf.getPage(pageNo);
        if (cancelled) return;
        const vp = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = vp.width;
        canvas.height = vp.height;

        // A page change while a render is in flight would otherwise paint the old page over the new one.
        if (renderTaskRef.current) { try { renderTaskRef.current.cancel(); } catch { /* already done */ } }
        const task = page.render({ canvasContext: canvas.getContext("2d"), viewport: vp });
        renderTaskRef.current = task;
        await task.promise;
        if (!cancelled) setViewport(vp);
      } catch (e) {
        // A cancelled render is the normal result of paging quickly; it is not an error to report.
        if (!cancelled && e && e.name !== "RenderingCancelledException") {
          setError(t("annotations.renderFailed", { defaultValue: "This page could not be drawn." }));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [pdf, pageNo, scale, open, t]);

  // ── Load the marks ──────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!open || !dbName || !documentId || !token) return;
    try {
      const data = await listAnnotations(token, dbName, documentId);
      setMarks(data?.annotations || []);
      setOnOtherVersions(data?.onOtherVersions || 0);
      setVersionNumber(data?.versionNumber ?? null);
    } catch (e) {
      setError(e?.code === "ANNOT_NOT_PDF"
        ? t("annotations.notPdf", {
            defaultValue: "Annotations are drawn in PDF page coordinates, so they are only offered on documents archived as PDF.",
          })
        : (e?.message || t("annotations.loadFailed", { defaultValue: "The annotations could not be loaded." })));
    }
  }, [open, dbName, documentId, token, t]);

  useEffect(() => { load(); }, [load]);

  // ── Coordinates ─────────────────────────────────────────────────────────────────────────
  // Canvas pixels → PDF points. The viewport knows the scale AND the page rotation, so this is the only
  // place that needs to understand either.
  const toPdf = useCallback((px, py) => {
    if (!viewport) return [0, 0];
    const [x, y] = viewport.convertToPdfPoint(px, py);
    return [x, y];
  }, [viewport]);

  // PDF points → canvas pixels, for drawing a stored mark back onto the page.
  const toPx = useCallback((x, y) => {
    if (!viewport) return [0, 0];
    const [px, py] = viewport.convertToViewportPoint(x, y);
    return [px, py];
  }, [viewport]);

  // A stored rect is x/y/width/height with y measured UP from the bottom. On screen y grows downwards, so
  // the top-left pixel corner comes from the rect's TOP edge (y + height).
  const rectToPx = useCallback((a) => {
    const [x1, y1] = toPx(a.x, a.y + a.height);
    const [x2, y2] = toPx(a.x + a.width, a.y);
    return { left: Math.min(x1, x2), top: Math.min(y1, y2), width: Math.abs(x2 - x1), height: Math.abs(y2 - y1) };
  }, [toPx]);

  const pageMarks = useMemo(() => marks.filter((m) => m.pageNumber === pageNo), [marks, pageNo]);

  const relPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  // ── Drawing ─────────────────────────────────────────────────────────────────────────────
  const onDown = (e) => {
    if (tool === "Select" || !viewport || busy) return;
    const [px, py] = relPos(e);
    if (tool === "Note") { setNoteDraft({ px, py, text: "" }); return; }
    if (tool === "Ink") { setInkPath([[px, py]]); return; }
    setDrag({ x0: px, y0: py, x1: px, y1: py });
  };

  const onMove = (e) => {
    if (!drag && !inkPath) return;
    const [px, py] = relPos(e);
    if (inkPath) {
      // Thinned to whole pixels: a raw pointer stream stores thousands of points that are visually
      // identical to a few hundred, and the path has to survive a round trip through the database.
      const last = inkPath[inkPath.length - 1];
      if (Math.abs(px - last[0]) >= 1 || Math.abs(py - last[1]) >= 1) setInkPath([...inkPath, [px, py]]);
      return;
    }
    setDrag({ ...drag, x1: px, y1: py });
  };

  const onUp = async () => {
    if (inkPath) {
      const path = inkPath;
      setInkPath(null);
      if (path.length >= 2) await commitInk(path);
      return;
    }
    if (!drag) return;
    const d = drag;
    setDrag(null);
    if (Math.abs(d.x1 - d.x0) < MIN_DRAG_PX || Math.abs(d.y1 - d.y0) < MIN_DRAG_PX) return; // a click
    await commitRect(d);
  };

  const commitRect = async (d) => {
    const [ax, ay] = toPdf(d.x0, d.y0);
    const [bx, by] = toPdf(d.x1, d.y1);
    await post({
      kind: tool,
      x: Math.min(ax, bx),
      y: Math.min(ay, by),
      width: Math.abs(bx - ax),
      height: Math.abs(by - ay),
    });
  };

  const commitInk = async (path) => {
    const pts = path.map(([px, py]) => toPdf(px, py).map((v) => Math.round(v * 100) / 100));
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    await post({
      kind: "Ink",
      // The bounding box is stored alongside the path so the list and any future search can reason about
      // where a stroke is without parsing every point.
      x: Math.min(...xs),
      y: Math.min(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
      pointsJson: JSON.stringify(pts),
    });
  };

  const commitNote = async () => {
    if (!noteDraft || !noteDraft.text.trim()) { setNoteDraft(null); return; }
    const [x, y] = toPdf(noteDraft.px, noteDraft.py);
    const text = noteDraft.text;
    setNoteDraft(null);
    await post({ kind: "Note", x, y, width: 0, height: 0, note: text });
  };

  const post = async (shape) => {
    setBusy(true);
    setError("");
    try {
      await addAnnotation(token, {
        databaseName: dbName,
        documentId,
        pageNumber: pageNo,
        color,
        ...shape,
      });
      await load();
    } catch (e) {
      setError(e?.message || t("annotations.saveFailed", { defaultValue: "The annotation could not be saved." }));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target) return;
    setBusy(true);
    try {
      await deleteAnnotation(token, target.id, dbName);
      setSelected(null);
      await load();
    } catch (e) {
      setError(e?.message || t("annotations.deleteFailed", { defaultValue: "The annotation could not be removed." }));
    } finally { setBusy(false); }
  };

  const recolour = async (mark, next) => {
    setBusy(true);
    try {
      await updateAnnotation(token, mark.id, { databaseName: dbName, color: next });
      await load();
    } catch (e) {
      setError(e?.message || t("annotations.saveFailed", { defaultValue: "The annotation could not be saved." }));
    } finally { setBusy(false); }
  };

  // Escape closes the viewer, unless a note is being typed — then it abandons the note first.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (noteDraft) { setNoteDraft(null); return; }
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, noteDraft, onClose]);

  if (!open) return null;

  const drawing = tool !== "Select";

  return createPortal(
    <div className="av-backdrop" role="dialog" aria-modal="true">
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="av-bar">
        <span className="av-name" title={docName}>{docName}</span>
        {versionNumber != null && <span className="av-version">v{versionNumber}</span>}

        <div className="av-tools">
          {TOOLS.map((k) => (
            <button
              key={k}
              type="button"
              className={`av-tool${tool === k ? " on" : ""}`}
              onClick={() => { setTool(k); setSelected(null); }}
              title={t(`annotations.tool${k}`, { defaultValue: k })}
            >
              {t(`annotations.tool${k}`, { defaultValue: k })}
            </button>
          ))}
        </div>

        <div className="av-colors">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`av-swatch${color === c ? " on" : ""}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
              aria-label={c}
            />
          ))}
        </div>

        <div className="av-pager">
          <button type="button" className="av-btn" disabled={pageNo <= 1} onClick={() => setPageNo(pageNo - 1)}>‹</button>
          <span className="av-page">{pageNo} / {pageCount || "…"}</span>
          <button type="button" className="av-btn" disabled={pageNo >= pageCount} onClick={() => setPageNo(pageNo + 1)}>›</button>
          <button type="button" className="av-btn" onClick={() => setScale(Math.max(0.5, +(scale - 0.25).toFixed(2)))}>−</button>
          <span className="av-zoom">{Math.round(scale * 100)}%</span>
          <button type="button" className="av-btn" onClick={() => setScale(Math.min(4, +(scale + 0.25).toFixed(2)))}>+</button>
        </div>

        <button type="button" className="av-btn av-close" onClick={onClose}>
          ✕ {t("common.close", { defaultValue: "Close" })}
        </button>
      </div>

      {error && <div className="av-error">{error}</div>}
      {loading && <div className="av-muted">{t("common.loading", { defaultValue: "Loading…" })}</div>}

      <div className="av-body">
        {/* ── Page + overlay ────────────────────────────────────────────── */}
        <div className="av-stage">
          <div
            className={`av-page-wrap${drawing ? " drawing" : ""}`}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={() => { setDrag(null); setInkPath(null); }}
          >
            <canvas ref={canvasRef} className="av-canvas" />

            {viewport && (
              <svg
                className="av-layer"
                width={viewport.width}
                height={viewport.height}
                // The layer must not eat clicks while the Select tool is active, or a mark could never
                // be picked; while drawing, the wrapper above handles the pointer instead.
                style={{ pointerEvents: "none" }}
              >
                {pageMarks.map((m) => {
                  const on = selected === m.id;
                  if (m.kind === "Ink") {
                    let pts = [];
                    try { pts = JSON.parse(m.pointsJson || "[]"); } catch { pts = []; }
                    const d = pts.map(([x, y], i) => {
                      const [px, py] = toPx(x, y);
                      return `${i === 0 ? "M" : "L"}${px.toFixed(1)},${py.toFixed(1)}`;
                    }).join(" ");
                    return (
                      <path key={m.id} d={d} fill="none" stroke={m.color || DEFAULT_COLOR}
                            strokeWidth={on ? 3.5 : 2} strokeLinecap="round" strokeLinejoin="round"
                            opacity={m.stale ? 0.35 : 0.95} />
                    );
                  }

                  const r = rectToPx(m);
                  if (m.kind === "Note") {
                    return (
                      <g key={m.id} opacity={m.stale ? 0.35 : 1}>
                        <circle cx={r.left} cy={r.top} r={on ? 9 : 7}
                                fill={m.color || DEFAULT_COLOR} stroke="#0f172a" strokeWidth="1" />
                      </g>
                    );
                  }
                  if (m.kind === "Strikeout") {
                    return (
                      <line key={m.id}
                            x1={r.left} y1={r.top + r.height / 2}
                            x2={r.left + r.width} y2={r.top + r.height / 2}
                            stroke={m.color || DEFAULT_COLOR} strokeWidth={on ? 3 : 2}
                            opacity={m.stale ? 0.35 : 0.95} />
                    );
                  }
                  const isHighlight = m.kind === "Highlight";
                  return (
                    <rect key={m.id}
                          x={r.left} y={r.top} width={r.width} height={r.height}
                          fill={isHighlight ? (m.color || DEFAULT_COLOR) : "none"}
                          fillOpacity={isHighlight ? 0.32 : 0}
                          stroke={m.color || DEFAULT_COLOR}
                          strokeWidth={isHighlight ? (on ? 2 : 0) : (on ? 3 : 1.6)}
                          opacity={m.stale ? 0.35 : 1} />
                  );
                })}

                {/* The rectangle currently being dragged out. */}
                {drag && (
                  <rect
                    x={Math.min(drag.x0, drag.x1)} y={Math.min(drag.y0, drag.y1)}
                    width={Math.abs(drag.x1 - drag.x0)} height={Math.abs(drag.y1 - drag.y0)}
                    fill={color} fillOpacity={tool === "Highlight" ? 0.3 : 0.08}
                    stroke={color} strokeWidth="1.5" strokeDasharray="4 3" />
                )}
                {inkPath && inkPath.length > 1 && (
                  <path d={inkPath.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ")}
                        fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
                )}
              </svg>
            )}

            {/* A sticky note is typed where it was dropped, so the text and its anchor are chosen together. */}
            {noteDraft && (
              <div className="av-note-draft" style={{ left: noteDraft.px, top: noteDraft.py }}>
                <textarea
                  autoFocus
                  className="av-note-input"
                  maxLength={2000}
                  placeholder={t("annotations.notePlaceholder", { defaultValue: "Note…" })}
                  value={noteDraft.text}
                  onChange={(e) => setNoteDraft({ ...noteDraft, text: e.target.value })}
                />
                <div className="av-note-actions">
                  <button type="button" className="av-btn primary" onClick={commitNote}>
                    {t("common.save", { defaultValue: "Save" })}
                  </button>
                  <button type="button" className="av-btn" onClick={() => setNoteDraft(null)}>
                    {t("common.cancel", { defaultValue: "Cancel" })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Mark list ─────────────────────────────────────────────────── */}
        <aside className="av-side">
          <div className="av-side-title">
            {t("annotations.onThisPage", { defaultValue: "On this page" })}
            <span className="av-count">{pageMarks.length}</span>
          </div>

          {pageMarks.length === 0 && (
            <div className="av-muted">
              {drawing
                ? t("annotations.drawHint", { defaultValue: "Drag on the page to draw." })
                : t("annotations.noneHere", { defaultValue: "Nothing marked on this page." })}
            </div>
          )}

          {pageMarks.map((m) => (
            <div
              key={m.id}
              className={`av-item${selected === m.id ? " on" : ""}`}
              onMouseEnter={() => setSelected(m.id)}
              onMouseLeave={() => setSelected(null)}
            >
              <div className="av-item-head">
                <span className="av-dot" style={{ background: m.color || DEFAULT_COLOR }} />
                <span className="av-kind">{t(`annotations.tool${m.kind}`, { defaultValue: m.kind })}</span>
                <span className="av-by">{m.createdBy}</span>
              </div>
              {m.note && <div className="av-item-note">{m.note}</div>}
              {m.stale && (
                <div className="av-stale">
                  {t("annotations.stale", {
                    defaultValue: "The file changed under this mark — its position may no longer be right.",
                  })}
                </div>
              )}
              <div className="av-item-actions">
                {m.canEdit && COLORS.map((c) => (
                  <button key={c} type="button" className="av-mini-swatch" style={{ background: c }}
                          onClick={() => recolour(m, c)} aria-label={c} disabled={busy} />
                ))}
                {m.canDelete && (
                  <button type="button" className="av-link danger" onClick={() => setConfirmDelete(m)} disabled={busy}>
                    {t("common.delete", { defaultValue: "Delete" })}
                  </button>
                )}
              </div>
            </div>
          ))}

          {onOtherVersions > 0 && (
            <div className="av-other">
              {t("annotations.onOtherVersions", {
                defaultValue: "{{count}} marks are on other versions of this document. They are not shown here, because they were measured against different pages.",
                count: onOtherVersions,
              })}
            </div>
          )}
        </aside>
      </div>

      <ConfirmModal
        open={!!confirmDelete}
        title={t("annotations.deleteTitle", { defaultValue: "Remove this annotation?" })}
        body={t("annotations.deleteBody", {
          defaultValue: "It stops being drawn for everyone. The record is kept, so markup on a document under legal hold is not lost.",
        })}
        variant="danger"
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>,
    document.body,
  );
}
